import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { navigate } from '../app/router';
import { cardAspect, NoteCard } from '../components/note/NoteCard';
import { Icon } from '../components/ui/Icon';
import { IconButton, Sheet, confirmDialog } from '../components/ui/ui';
import { resolveLook } from '../designs/look';
import { removeBackground } from '../images/imageTools';
import { importImage } from '../images/imageTools';
import { relativeTime, formatDateTime } from '../lib/format';
import { uid } from '../lib/id';
import { checklistProgress, displayTitle, lineOps, newLine } from '../notes/model';
import { nextOccurrence } from '../scheduling/recurrence';
import { store, useStore } from '../state/store';
import { getAsset, putAsset } from '../storage/repo';
import type { Asset, Line, Note, NoteImage, Tool } from '../storage/schemas';
import { ChecklistSheet } from './ChecklistSheet';
import { DesignGallery } from '../designs/DesignGallery';
import { SettingsSheet } from '../settings/SettingsSheet';
import { TEMPLATES } from '../notes/templates';
import { DesignStackButton } from './DesignStackButton';
import { TextSheet } from './TextSheet';
import { DrawingSurface } from './DrawingSurface';
import { EditableImages } from './EditableImages';
import { EditableLines, type EditableLinesHandle } from './EditableLines';
import { ImageSheet } from './ImageSheet';
import { MoreSheet } from './MoreSheet';
import { CropSheet, DrawingToolbar, EditorToolbar, ImageToolbar } from './Toolbars';
import { useAutoFit } from './useAutoFit';
import { useNoteEditing } from './useNoteEditing';
import './editor.css';

type SheetName = 'none' | 'gallery' | 'text' | 'settings' | 'image' | 'checklist' | 'trash' | 'more' | 'crop';

function SaveIndicator({ noteId }: { noteId: string }) {
  const status = useStore((s) => s.saveStatus[noteId] ?? 'saved');
  const error = useStore((s) => s.saveError);
  // The reference shows no save status (everything auto-saves); MemoNest only surfaces failures.
  if (status !== 'error') return <span className="sr-only" role="status" aria-live="polite">{status === 'saved' ? 'Saved' : ''}</span>;
  const label = 'Not saved';
  return (
    <span className={`save-indicator is-${status}`} role="status" aria-live="polite" title={status === 'error' ? (error ?? '') : undefined}>
      <span className="save-dot" aria-hidden="true" />
      {label}
      {status === 'error' && (
        <button type="button" className="link-btn" onClick={() => store.retrySave(noteId)}>
          Retry
        </button>
      )}
    </span>
  );
}

export function EditorScreen({ noteId }: { noteId: string }) {
  const note = useStore((s) => s.notes[noteId]) as Note | undefined;
  const designs = useStore((s) => s.designs);
  const activeId = useStore((s) => s.active.noteId);
  const activeSource = useStore((s) => s.active.source);
  const schedules = useStore((s) => s.schedules);
  const editing = useNoteEditing(noteId);
  const linesRef = useRef<EditableLinesHandle>(null);
  const textRef = useRef<HTMLDivElement>(null);
  const cardRef = useRef<HTMLDivElement>(null);
  const [sheet, setSheet] = useState<SheetName>('none');
  const [focusedLine, setFocusedLine] = useState<string | null>(null);
  const lastFocusedLine = useRef<string | null>(null);
  const [drawing, setDrawing] = useState(false);
  const [tool, setTool] = useState<Tool>('pen');
  const [penColor, setPenColor] = useState('#FF8700');
  const [penSize, setPenSize] = useState(0.008);
  const [selectedImage, setSelectedImage] = useState<string | null>(null);

  useEffect(() => {
    if (focusedLine) lastFocusedLine.current = focusedLine;
  }, [focusedLine]);

  const look = useMemo(() => (note ? resolveLook(note, designs) : null), [note, designs]);

  useAutoFit(
    textRef,
    note ?? ({ lines: [], style: { autoSize: false } } as unknown as Note),
    look?.font ?? 'schoolbell',
    (scale) => store.updateNote(noteId, (n) => ({ ...n, style: { ...n.style, fontScale: scale } }), { touch: false }),
    !!note && !drawing,
  );

  // Desktop shortcuts. Native text undo is replaced by the note-level history so structure edits undo too.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const mod = e.ctrlKey || e.metaKey;
      if (e.isComposing) return;
      if (mod && !e.altKey && e.key.toLowerCase() === 'z') {
        e.preventDefault();
        if (e.shiftKey) editing.redo();
        else editing.undo();
      } else if (mod && e.key.toLowerCase() === 'y') {
        e.preventDefault();
        editing.redo();
      } else if (mod && e.key.toLowerCase() === 's') {
        e.preventDefault();
        void store.flush().then(() => store.toast('Saved.', 'success'));
      } else if (mod && e.shiftKey && e.key.toLowerCase() === 'l') {
        e.preventDefault();
        toggleChecklist();
      } else if (e.key === 'Escape' && drawing && sheet === 'none') {
        setDrawing(false);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  });

  const addImageAsset = useCallback(
    (asset: Pick<Asset, 'id' | 'width' | 'height'>) => {
      const aspect = asset.width && asset.height ? asset.height / asset.width : 1;
      const cardAr = note ? cardAspect(note) : 2.2;
      // Fit inside ~80% of the card height.
      const w = Math.min(0.42, (0.8 / cardAr) / aspect);
      const img: NoteImage = {
        id: uid('i'),
        assetId: asset.id,
        x: 0.68,
        y: 0.5,
        w,
        aspect,
        rotation: 0,
        crop: { t: 0, r: 0, b: 0, l: 0 },
        z: (note?.images.reduce((m, i) => Math.max(m, i.z), 0) ?? 0) + 1,
        locked: false,
        aboveText: true,
      };
      editing.edit((n) => ({ ...n, images: [...n.images, img] }), 'image');
      setSelectedImage(img.id);
    },
    [editing, note],
  );

  // Paste images from the clipboard.
  useEffect(() => {
    const onPaste = async (e: ClipboardEvent) => {
      const file = [...(e.clipboardData?.files ?? [])].find((f) => f.type.startsWith('image/'));
      if (!file) return;
      e.preventDefault();
      try {
        const asset = await putAsset(await importImage(file, file.name || 'Pasted image'));
        addImageAsset(asset);
      } catch (err) {
        store.toast((err as Error).message, 'error');
      }
    };
    document.addEventListener('paste', onPaste);
    return () => document.removeEventListener('paste', onPaste);
  }, [addImageAsset]);

  if (!note || !look) {
    return (
      <div className="screen">
        <p className="empty-state">This note no longer exists.</p>
        <button type="button" className="btn" onClick={() => navigate({ name: 'home' })}>
          Go to current note
        </button>
      </div>
    );
  }

  const aspect = cardAspect(note);
  const progress = checklistProgress(note);
  const isActive = activeId === note.id;
  const noteSchedules = schedules.filter((s) => s.noteId === note.id && s.enabled);
  const nextRun = noteSchedules
    .map((s) => nextOccurrence(s.rule, Date.now()))
    .filter((t): t is number => t !== null)
    .sort((a, b) => a - b)[0];
  const selected = note.images.find((i) => i.id === selectedImage) ?? null;

  // While typing: toggle the current line. Not typing: toggle the whole note (reference: a list of checkboxes).
  function toggleChecklist() {
    const id = focusedLine;
    const ids = id && note!.lines.some((l) => l.id === id) ? [id] : [];
    editing.edit((n) => ({ ...n, lines: lineOps.toggleChecklist(n.lines, ids) }), 'structure');
    if (id) requestAnimationFrame(() => linesRef.current?.focusLine(id, 'end'));
  }

  // Live drag: update in place (the gesture's first frame already recorded an undo step).
  const updateImage = (id: string, patch: Partial<NoteImage>) =>
    store.updateNote(noteId, (n) => ({ ...n, images: n.images.map((i) => (i.id === id ? { ...i, ...patch } : i)) }));

  const imagePatch = (patch: Partial<NoteImage>) => {
    if (!selected) return;
    editing.edit((n) => ({ ...n, images: n.images.map((i) => (i.id === selected.id ? { ...i, ...patch } : i)) }), `img:${selected.id}:${Object.keys(patch).join()}`);
  };

  const layer = (dir: 1 | -1) => {
    if (!selected) return;
    editing.edit((n) => {
      const sorted = [...n.images].sort((a, b) => a.z - b.z);
      const i = sorted.findIndex((x) => x.id === selected.id);
      const j = i + dir;
      if (j < 0 || j >= sorted.length) return n;
      [sorted[i], sorted[j]] = [sorted[j], sorted[i]];
      const z = new Map(sorted.map((x, k) => [x.id, k + 1]));
      return { ...n, images: n.images.map((x) => ({ ...x, z: z.get(x.id)! })) };
    }, 'layer');
  };

  const cutOut = async () => {
    if (!selected) return;
    try {
      const a = await getAsset(selected.assetId);
      if (!a) throw new Error('Image data is missing.');
      const out = await putAsset(await removeBackground(a));
      imagePatch({ assetId: out.id });
      store.toast('Background removed. Undo to bring it back.', 'success');
    } catch (err) {
      store.toast(`Could not cut out: ${(err as Error).message}`, 'error');
    }
  };

  const startNewPage = () => {
    const tplId = store.get().settings.newPageTemplate;
    const tpl = TEMPLATES.find((t) => t.id === tplId);
    const lines = tpl
      ? tpl.lines.map((l) => ({ ...newLine(l.text, l.kind), marks: l.heading ? { heading: true } : undefined }))
      : [newLine('', tplId === 'checklist' ? 'check' : 'text')];
    const n = store.createNote({ designId: note.designId, lines, style: { ...note.style, fontScale: 7 } });
    if (isActive) store.setActive(n.id);
    setSheet('none');
    navigate(isActive ? { name: 'home' } : { name: 'note', id: n.id }, true);
    store.toast('New page started. The previous one is in History.', 'success');
  };

  const clearNote = () => {
    editing.edit((n) => ({ ...n, lines: [newLine()], images: [], strokes: [] }), 'structure');
    setSheet('none');
    store.toast('Note cleared.', 'info', { label: 'Undo', run: () => editing.undo() });
  };

  const moveToTrash = async () => {
    setSheet('none');
    if (!(await confirmDialog({ title: 'Move this note to Trash?', message: 'You can restore it from History → Trash.', confirmLabel: 'Move to Trash', danger: true }))) return;
    await store.trashNote(note.id);
    store.toast('Moved to Trash.', 'info', { label: 'Undo', run: () => store.restoreNote(note.id) });
    navigate({ name: 'home' }, true);
  };

  const scheduleItem = (line: Line) => {
    if (!line.due) return;
    setSheet('none');
    navigate({ name: 'schedule', id: `new:${note.id}:${line.due}:${encodeURIComponent(line.text.slice(0, 60))}` });
  };

  const onCardPointerDown = (e: React.PointerEvent) => {
    if (drawing) return;
    const target = e.target as HTMLElement;
    if (target.closest('textarea, button, .nc-image')) return;
    setSelectedImage(null);
    // Tapping blank paper focuses the end of the note (reference: "just open and write").
    e.preventDefault();
    linesRef.current?.focusEnd();
  };

  return (
    <div className={`editor ${drawing ? 'is-drawing' : ''} ${focusedLine || selected ? 'is-typing' : ''}`}>
      <header className="editor-top no-print">
        <DesignStackButton note={note} onClick={() => setSheet('gallery')} />
        <div className="editor-top-center">
          <SaveIndicator noteId={note.id} />
        </div>
        <IconButton icon="settings" label="Settings" onClick={() => setSheet('settings')} className="editor-gear" />
      </header>

      {note.deletedAt && (
        <div className="editor-banner" role="status">
          This note is in Trash.
          <button type="button" className="btn is-small" onClick={() => store.restoreNote(note.id)}>
            Restore
          </button>
        </div>
      )}

      <div className="editor-card-wrap" ref={cardRef} onPointerDown={onCardPointerDown}>
        <NoteCard
          note={note}
          look={look}
          className={`editor-card ${drawing ? 'is-drawing' : ''}`}
          style={{ ['--rim' as string]: 'var(--app-rim)' }}
          textLayer={
            <EditableLines ref={linesRef} textRef={textRef} note={note} ink={look.ink} editing={editing} onFocusChange={setFocusedLine} />
          }
          imageLayer={(above) => (
            <EditableImages
              images={note.images}
              above={above}
              aspect={aspect}
              selectedId={selectedImage}
              onSelect={setSelectedImage}
              onChange={(id, patch, commit) => {
                if (commit) {
                  dragStarted.current = false;
                  return;
                }
                // First frame of a gesture records one undo step; later frames update in place.
                if (!dragStarted.current) {
                  dragStarted.current = true;
                  editing.edit((n) => n, 'misc');
                }
                updateImage(id, patch);
              }}
              cardRef={cardRef}
              interactive={!drawing}
            />
          )}
          drawingLayer={
            <DrawingSurface
              strokes={note.strokes}
              active={drawing}
              tool={tool}
              color={penColor}
              size={penSize}
              onStroke={(s) => editing.edit((n) => ({ ...n, strokes: [...n.strokes, s] }), 'misc')}
            />
          }
        />
      </div>

      <div className="editor-controls no-print">
        {drawing ? (
          <DrawingToolbar
            onClose={() => setDrawing(false)}
            onUndo={editing.undo}
            onRedo={editing.redo}
            canUndo={editing.canUndo}
            canRedo={editing.canRedo}
            tool={tool}
            setTool={setTool}
            color={penColor}
            setColor={setPenColor}
            size={penSize}
            setSize={setPenSize}
          />
        ) : selected ? (
          <ImageToolbar
            image={selected}
            onChange={imagePatch}
            onLayer={layer}
            onDelete={() => {
              editing.edit((n) => ({ ...n, images: n.images.filter((i) => i.id !== selected.id) }), 'image');
              setSelectedImage(null);
            }}
            onCrop={() => setSheet('crop')}
            onCutOut={() => void cutOut()}
            onDone={() => setSelectedImage(null)}
          />
        ) : (
          <EditorToolbar
            onChecklist={toggleChecklist}
            onChecklistLong={() => setSheet('checklist')}
            checklistActive={note.lines.some((l) => l.id === lastFocusedLine.current && l.kind === 'check')}
            onText={() => setSheet('text')}
            onDraw={() => {
              (document.activeElement as HTMLElement | null)?.blur();
              setSelectedImage(null);
              setDrawing(true);
            }}
            onImage={() => setSheet('image')}
            onTrash={() => setSheet('trash')}
            penColor={penColor}
          />
        )}

      </div>

      {/* The reference home screen shows only the note. MemoNest adds context only when it matters:
          an earlier page opened from History, or a note put on screen by a schedule. */}
      {!drawing && (!isActive || activeSource === 'schedule' || nextRun) && (
        <div className="editor-meta no-print">
          {!isActive && (
            <>
              <button type="button" className="meta-chip" onClick={() => store.setActive(note.id)}>
                <Icon name="phone" size={16} />
                Show on lock screen
              </button>
              <button type="button" className="meta-chip" onClick={() => navigate({ name: 'home' })}>
                <Icon name="back" size={16} />
                Current note
              </button>
            </>
          )}
          {isActive && activeSource === 'schedule' && <span className="meta-chip is-quiet">Shown by a schedule</span>}
          {nextRun && (
            <button type="button" className="meta-chip is-quiet" onClick={() => navigate({ name: 'schedules', noteId: note.id })}>
              <Icon name="alarm" size={16} />
              {formatDateTime(nextRun)}
            </button>
          )}
          <span className="sr-only">
            Edited {relativeTime(note.updatedAt)}. {progress.total > 0 ? `${progress.done} of ${progress.total} done.` : ''}
          </span>
        </div>
      )}

      <TextSheet open={sheet === 'text'} onClose={() => setSheet('none')} note={note} editing={editing} focusedLineId={lastFocusedLine.current} />
      <DesignGallery open={sheet === 'gallery'} onClose={() => setSheet('none')} note={note} editing={editing} />
      <SettingsSheet open={sheet === 'settings'} onClose={() => setSheet('none')} onNoteOptions={() => setSheet('more')} onNewPage={startNewPage} />
      <ImageSheet open={sheet === 'image'} onClose={() => setSheet('none')} onAdd={addImageAsset} />
      <ChecklistSheet open={sheet === 'checklist'} onClose={() => setSheet('none')} note={note} editing={editing} onScheduleItem={scheduleItem} />
      <CropSheet open={sheet === 'crop'} onClose={() => setSheet('none')} image={selected} onChange={imagePatch} />
      <MoreSheet open={sheet === 'more'} onClose={() => setSheet('none')} note={note} title={displayTitle(note)} />
      <Sheet open={sheet === 'trash'} onClose={() => setSheet('none')} title="This page">
        <div className="action-list">
          <button type="button" className="action" onClick={startNewPage}>
            <Icon name="plus" />
            <span>
              Start a new page
              <small>This note stays in History</small>
            </span>
          </button>
          <button type="button" className="action" onClick={clearNote}>
            <Icon name="eraser" />
            <span>
              Clear this note
              <small>Removes text, images and drawing (undo available)</small>
            </span>
          </button>
          <button type="button" className="action is-danger" onClick={() => void moveToTrash()}>
            <Icon name="trash" />
            <span>
              Move to Trash
              <small>Restore any time from History</small>
            </span>
          </button>
        </div>
      </Sheet>
    </div>
  );
}

// Module-level flag shared by gesture callbacks (one gesture at a time).
const dragStarted = { current: false };
